import { Component } from "@angular/core";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { EMPTY, from, Observable, of } from "rxjs";
import { mergeMap, switchMap, tap, toArray } from "rxjs/operators";
import { Reference } from "src/app/inventory/reference/reference";
import { ReferenceService } from "src/app/inventory/reference/reference.service";
import { ReferenceSearchDialogComponent } from "src/app/inventory/reference/search/reference-search-dialog.component";
import { Page } from "src/app/util/page";
import { Criteria } from "src/app/util/search/criteria/criteria";
import { Restock, RestockDetail } from "../restock";
import { RestockService } from "../restock.service";

@Component({
  styleUrls: ['./restock-details.component.css'],
  templateUrl: './restock-details.component.html',
})
export class RestockDetailComponent {
  public query = "";
  public restock!: Restock;

  public loading = false;
  public processing = false;
  public searchInProgress = false;

  constructor(
    private modalService: NgbModal,
    private restockService: RestockService,
    private referenceService: ReferenceService,
    private router: Router,
    activatedRoute: ActivatedRoute,
  ) {
    activatedRoute.paramMap.pipe(
      switchMap(paramMap => this.getRestockIdFromParams(paramMap)),
      switchMap(restockId => this.refreshRestock(restockId))
    ).subscribe();
  }

  private addReferenceToRestock(reference: Reference) {
    this.restockService.addReferenceToRestock(reference.isbn, 1)
      .pipe(switchMap(() => this.refreshRestock(this.restock.id)))
      .subscribe();
  }

  public completeRestock() {
    this.loading = true;
    this.processing = true;
    this.restockService.complete(this.restock.id)

      .subscribe({
        error: _ => {
          this.loading = false;
          this.processing = false;
        },
        next: _ => this.router.navigateByUrl("/purchase/order")
      });
  }

  public onSearchKeyup(event: KeyboardEvent) {
    if (event.key != "Enter") {
      return;
    }

    this.search();
  }

  public openSearchDialog(references: Page<Reference>) {
    let modalRef = this.modalService.open(ReferenceSearchDialogComponent, { size: 'xl' });

    var component = this;
    modalRef.componentInstance.onAddReferences = (references: Reference[]) => from(references)
      .pipe(
        mergeMap(reference => component.restockService.addReferenceToRestock(reference.isbn, 1)),
        toArray(),
        switchMap(_ => component.refreshRestock(component.restock.id))
      );

    this.referenceService.initSearchResult({ initialResults: references, query: this.query });
  }

  private getRestockIdFromParams(paramMap: ParamMap): Observable<string> {
    let restockId = paramMap.get("restock");
    if (restockId == null) {
      this.router.navigateByUrl("/404");

      return EMPTY;
    }

    return of(restockId);
  }

  private onSearchResponse(references: Page<Reference>) {
    this.query = "";
    if (references.numberOfElements != 1) {
      this.openSearchDialog(references)
    } else {
      this.addReferenceToRestock(references.content[0]);
    }
  }

  private refreshRestock(restockId: string): Observable<Restock> {
    return this.restockService.findById(restockId)

      .pipe(tap(restock => this.restock = restock));
  }

  public removeDetail(detail: RestockDetail) {
    this.loading = true;

    this.restockService.removeDetail(this.restock.id, detail.reference.isbn)

      .pipe(switchMap(() => this.refreshRestock(this.restock.id)))

      .subscribe({
        error: _ => this.loading = false,
        next: _ => this.loading = false
      });
  }

  public search() {
    this.searchInProgress = true;
    this.loading = true;

    this.referenceService.search({
      query: this.query,
      author: [],
      genre: [],
      publisher: [],
      supplier: [],
    }, 0, { imageMaxWidth: 35 }).subscribe({
      error: _ => {
        this.loading = false;
        this.searchInProgress = false;
      },
      next: references => {
        this.loading = false;
        this.searchInProgress = false;

        this.onSearchResponse(references);
      }
    });
  }

  public updateQuantity(detail: RestockDetail) {
    this.loading = true;

    this.restockService.updateQuantity(this.restock.id, detail.reference.isbn, detail.quantity)

      .subscribe({
        error: _ => this.loading = false,
        next: _ => this.loading = false
      });
  }
}